import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Eye, Star } from "lucide-react";
import { GetResourceSearchResultsItem } from "@/api/getResourceSearchResults/types";
import { useAppDispatch } from "@/app/hooks";
import { setScrollPosition } from "@/modules/hub/features/ResourceLibrary/slices/resourceLibrarySlice";
import { FavoriteButton } from "../FavoriteButton/FavoriteButton";
import { toggleResourceFavorite } from "@/api/favoriteResource/favoriteResourceApi";
import { trackResourceCardClick } from "@/modules/hub/features/ResourceLibrary/analytics/resourceLibraryAnalytics";
import { trackPodcastCardClick } from "@/modules/hub/features/Podcasts/analytics/podcastsAnalytics";
import { trackNewsCardClick } from "@/modules/hub/features/News/analytics/newsAnalytics";

interface ResourceCardProps {
  resource: GetResourceSearchResultsItem;
  showFeaturedBadge?: boolean;
  variant?: "default" | "featured";
  onToggleFavorite?: (uuid: string) => void;
}

export function ResourceCard({
  resource,
  showFeaturedBadge = false,
  variant = "default",
  onToggleFavorite,
}: ResourceCardProps) {
  const dispatch = useAppDispatch();
  const isFavorited = resource.isFavorited ?? false;

  const DESCRIPTION_MAX_LENGTH = variant === "featured" ? 80 : 120;
  const contentType = (resource.resourceType || "document").toLowerCase();

  const handleCardClick = () => {
    const scrollY =
      window.scrollY ||
      window.pageYOffset ||
      document.documentElement.scrollTop ||
      document.body.scrollTop;

    // Save to Redux (legacy)
    dispatch(setScrollPosition(scrollY));

    // Save to sessionStorage for restoration on back button
    const state = {
      scrollY,
      timestamp: Date.now(),
    };
    sessionStorage.setItem(
      "resourceLibrary_scrollState",
      JSON.stringify(state),
    );

    // Track analytics based on resource type
    if (resource.resourceType === "Podcast") {
      trackPodcastCardClick({
        resourceType: resource.resourceType,
        resourceUuid: resource.uuid,
      });
    } else if (resource.resourceType === "News") {
      trackNewsCardClick({
        resourceType: resource.resourceType,
        resourceUuid: resource.uuid,
      });
    } else {
      trackResourceCardClick({
        resourceType: resource.resourceType || "Unknown",
        resourceUuid: resource.uuid,
      });
    }
  };

  const handleToggleFavorite = async (
    e: React.MouseEvent<HTMLButtonElement>,
  ) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      // Call API to toggle favorite
      await toggleResourceFavorite({ resourceUuid: resource.uuid });

      // Notify parent to update state and refresh favorites if needed
      if (onToggleFavorite) {
        await onToggleFavorite(resource.uuid);
      }
    } catch (error) {
      // Error handling is done in FavoriteButton
      throw error;
    }
  };

  // Check if this is a Partner Page that should open externally
  const isPartnerPage =
    resource.resourceType === "Partner Page" && resource.contentUrl;

  // Featured variant styles
  if (variant === "featured") {
    return (
      <div className="block group relative">
        {/* Optional Featured Badge */}
        {showFeaturedBadge && resource.isFeatured && (
          <div className="absolute -top-2 -right-2" style={{ zIndex: 1 }}>
            <div className="bg-yellow-400 dark:bg-yellow-500 text-yellow-900 dark:text-yellow-100 text-xs font-bold px-2 py-0.5 rounded-full shadow-md flex items-center gap-1">
              <Star className="h-3 w-3 fill-current" />
              <span className="uppercase text-[10px]">Featured</span>
            </div>
          </div>
        )}

        {/* Favorite Button */}
        <div className="absolute top-2 left-2" style={{ zIndex: 1 }}>
          <FavoriteButton
            isFavorited={isFavorited}
            onToggle={handleToggleFavorite}
          />
        </div>

        {isPartnerPage ? (
          <a
            className="block"
            href={resource.contentUrl || ""}
            rel="noopener noreferrer"
            target="_blank"
          >
            <div className="flex">
              {/* Thumbnail - smaller and as a side element */}
              <div className="relative w-24 flex-shrink-0">
                {resource.thumbnailUrl ? (
                  <img
                    alt={resource.title}
                    className="object-cover rounded-l-lg w-full h-full"
                    src={resource.thumbnailUrl}
                  />
                ) : resource.resourceType === "File" ? (
                  <div className="w-full h-full rounded-l-lg flex items-center justify-center bg-gradient-to-b from-red-300 to-red-400 dark:from-red-400 dark:to-red-600 relative overflow-hidden">
                    <svg
                      className="absolute inset-0 w-full h-full opacity-20"
                      preserveAspectRatio="none"
                      viewBox="0 0 100 100"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M0,30 Q25,20 50,30 T100,30 L100,100 L0,100 Z"
                        fill="white"
                      />
                      <path
                        d="M0,60 Q25,50 50,60 T100,60 L100,100 L0,100 Z"
                        fill="white"
                        opacity="0.5"
                      />
                    </svg>
                    <span
                      className="inline-block scale-75 relative z-10"
                      dangerouslySetInnerHTML={{
                        __html: resource.primaryIcon ?? "",
                      }}
                      style={{
                        filter: "brightness(0) invert(1)",
                        opacity: 0.9,
                      }}
                    />
                  </div>
                ) : (
                  <div className="w-full h-full rounded-l-lg flex items-center justify-center bg-gray-100 dark:bg-slate-700">
                    <span
                      className="inline-block scale-75"
                      dangerouslySetInnerHTML={{
                        __html: resource.primaryIcon ?? "",
                      }}
                    />
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="flex-1 p-3 flex flex-col justify-between min-w-0">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span
                      className="text-xs font-medium px-1.5 py-0.5 rounded-full"
                      style={{
                        backgroundColor: resource.backgroundColor ?? "#ccc",
                        color: resource.textColor ?? "#000",
                        border: resource.borderColor
                          ? `1px solid ${resource.borderColor}`
                          : "none",
                      }}
                    >
                      {contentType.charAt(0).toUpperCase() +
                        contentType.slice(1)}
                    </span>
                  </div>

                  <h3 className="font-semibold text-sm line-clamp-2">
                    {resource.title}
                  </h3>
                </div>
              </div>
            </div>
          </a>
        ) : (
          <Link
            className="block"
            onClick={handleCardClick}
            to={`/hub/resources/${resource.slug}`}
          >
            <div className="flex">
              {/* Thumbnail - smaller and as a side element */}
              <div className="relative w-24 flex-shrink-0">
                {resource.thumbnailUrl ? (
                  <img
                    alt={resource.title}
                    className="object-cover rounded-l-lg w-full h-full"
                    src={resource.thumbnailUrl}
                  />
                ) : resource.resourceType === "File" ? (
                  <div className="w-full h-full rounded-l-lg flex items-center justify-center bg-gradient-to-b from-red-300 to-red-400 dark:from-red-400 dark:to-red-600 relative overflow-hidden">
                    <svg
                      className="absolute inset-0 w-full h-full opacity-20"
                      preserveAspectRatio="none"
                      viewBox="0 0 100 100"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M0,30 Q25,20 50,30 T100,30 L100,100 L0,100 Z"
                        fill="white"
                      />
                      <path
                        d="M0,60 Q25,50 50,60 T100,60 L100,100 L0,100 Z"
                        fill="white"
                        opacity="0.5"
                      />
                    </svg>
                    <span
                      className="inline-block scale-75 relative z-10"
                      dangerouslySetInnerHTML={{
                        __html: resource.primaryIcon ?? "",
                      }}
                      style={{
                        filter: "brightness(0) invert(1)",
                        opacity: 0.9,
                      }}
                    />
                  </div>
                ) : (
                  <div className="w-full h-full rounded-l-lg flex items-center justify-center bg-gray-100 dark:bg-slate-700">
                    <span
                      className="inline-block scale-75"
                      dangerouslySetInnerHTML={{
                        __html: resource.primaryIcon ?? "",
                      }}
                    />
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="flex-1 p-3 flex flex-col justify-between min-w-0">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span
                      className="text-xs font-medium px-1.5 py-0.5 rounded-full"
                      style={{
                        backgroundColor: resource.backgroundColor ?? "#ccc",
                        color: resource.textColor ?? "#000",
                        border: resource.borderColor
                          ? `1px solid ${resource.borderColor}`
                          : "none",
                      }}
                    >
                      {contentType.charAt(0).toUpperCase() +
                        contentType.slice(1)}
                    </span>
                  </div>

                  <h3 className="font-semibold text-sm line-clamp-2">
                    {resource.title}
                  </h3>
                </div>
              </div>
            </div>
          </Link>
        )}
      </div>
    );
  }

  // Default variant (original style)
  return (
    <div className="block group relative">
      {/* Optional Featured Badge */}
      {showFeaturedBadge && resource.isFeatured && (
        <div className="absolute -top-2 -right-2" style={{ zIndex: 1 }}>
          <div className="bg-yellow-400 dark:bg-yellow-500 text-yellow-900 dark:text-yellow-100 text-xs font-bold px-3 py-1 rounded-full shadow-lg flex items-center gap-1">
            <Star className="h-3 w-3 fill-current" />
            FEATURED
          </div>
        </div>
      )}

      {/* Favorite Button */}
      <div className="absolute top-2 left-2" style={{ zIndex: 1 }}>
        <FavoriteButton
          isFavorited={isFavorited}
          onToggle={handleToggleFavorite}
        />
      </div>

      {isPartnerPage ? (
        <a
          className="block"
          href={resource.contentUrl || ""}
          rel="noopener noreferrer"
          target="_blank"
        >
          <div className="relative aspect-video overflow-hidden rounded-lg">
            {resource.thumbnailUrl ? (
              <img
                alt={resource.title}
                className="object-cover w-full h-full transition-transform duration-300 group-hover:scale-105"
                src={resource.thumbnailUrl}
              />
            ) : resource.resourceType === "File" ? (
              <div className="w-full h-full flex items-center justify-center bg-gradient-to-b from-red-300 to-red-400 dark:from-red-400 dark:to-red-600 relative overflow-hidden">
                <svg
                  className="absolute inset-0 w-full h-full opacity-20"
                  preserveAspectRatio="none"
                  viewBox="0 0 100 100"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M0,30 Q25,20 50,30 T100,30 L100,100 L0,100 Z"
                    fill="white"
                  />
                  <path
                    d="M0,60 Q25,50 50,60 T100,60 L100,100 L0,100 Z"
                    fill="white"
                    opacity="0.5"
                  />
                  <path
                    d="M0,85 Q25,80 50,85 T100,85 L100,100 L0,100 Z"
                    fill="white"
                    opacity="0.3"
                  />
                </svg>
                <span
                  className="inline-block relative z-10"
                  dangerouslySetInnerHTML={{
                    __html: resource.primaryIcon ?? "",
                  }}
                  style={{
                    filter: "brightness(0) invert(1)",
                    opacity: 0.9,
                  }}
                />
              </div>
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gray-100 dark:bg-slate-700">
                <span
                  className="inline-block"
                  dangerouslySetInnerHTML={{
                    __html: resource.primaryIcon ?? "",
                  }}
                />
              </div>
            )}
          </div>

          <div className="pt-3 space-y-2">
            <div className="flex items-center gap-2">
              <span
                className="text-xs font-medium px-2 py-1 rounded-full"
                style={{
                  backgroundColor: resource.backgroundColor ?? "#ccc",
                  color: resource.textColor ?? "#000",
                  border: resource.borderColor
                    ? `1px solid ${resource.borderColor}`
                    : "none",
                }}
              >
                {contentType.charAt(0).toUpperCase() + contentType.slice(1)}
              </span>
            </div>

            <h3 className="font-medium text-gray-800 dark:text-slate-200 group-hover:text-gray-900 dark:group-hover:text-white transition-colors">
              {resource.title}
            </h3>

            <div className="mt-4 text-sm text-gray-500 dark:text-slate-400">
              <span className="flex items-center gap-1">
                <Eye className="h-4 w-4" />
                {resource.viewCount} views
              </span>
            </div>
          </div>
        </a>
      ) : (
        <Link
          className="block"
          onClick={handleCardClick}
          to={`/hub/resources/${resource.slug}`}
        >
          <div className="relative aspect-video overflow-hidden rounded-lg">
            {resource.thumbnailUrl ? (
              <img
                alt={resource.title}
                className="object-cover w-full h-full transition-transform duration-300 group-hover:scale-105"
                src={resource.thumbnailUrl}
              />
            ) : resource.resourceType === "File" ? (
              <div className="w-full h-full flex items-center justify-center bg-gradient-to-b from-red-300 to-red-400 dark:from-red-400 dark:to-red-600 relative overflow-hidden">
                <svg
                  className="absolute inset-0 w-full h-full opacity-20"
                  preserveAspectRatio="none"
                  viewBox="0 0 100 100"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M0,30 Q25,20 50,30 T100,30 L100,100 L0,100 Z"
                    fill="white"
                  />
                  <path
                    d="M0,60 Q25,50 50,60 T100,60 L100,100 L0,100 Z"
                    fill="white"
                    opacity="0.5"
                  />
                  <path
                    d="M0,85 Q25,80 50,85 T100,85 L100,100 L0,100 Z"
                    fill="white"
                    opacity="0.3"
                  />
                </svg>
                <span
                  className="inline-block relative z-10"
                  dangerouslySetInnerHTML={{
                    __html: resource.primaryIcon ?? "",
                  }}
                  style={{
                    filter: "brightness(0) invert(1)",
                    opacity: 0.9,
                  }}
                />
              </div>
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gray-100 dark:bg-slate-700">
                <span
                  className="inline-block"
                  dangerouslySetInnerHTML={{
                    __html: resource.primaryIcon ?? "",
                  }}
                />
              </div>
            )}
          </div>

          <div className="pt-3 space-y-2">
            <div className="flex items-center gap-2">
              <span
                className="text-xs font-medium px-2 py-1 rounded-full"
                style={{
                  backgroundColor: resource.backgroundColor ?? "#ccc",
                  color: resource.textColor ?? "#000",
                  border: resource.borderColor
                    ? `1px solid ${resource.borderColor}`
                    : "none",
                }}
              >
                {contentType.charAt(0).toUpperCase() + contentType.slice(1)}
              </span>
            </div>

            <h3 className="font-medium text-gray-800 dark:text-slate-200 group-hover:text-gray-900 dark:group-hover:text-white transition-colors">
              {resource.title}
            </h3>

            <div className="mt-4 text-sm text-gray-500 dark:text-slate-400">
              <span className="flex items-center gap-1">
                <Eye className="h-4 w-4" />
                {resource.viewCount} views
              </span>
            </div>
          </div>
        </Link>
      )}
    </div>
  );
}
